'use client';

import { useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import { isInApp } from '@/libs/comeAppSDK';
import { swipeBackTriggerAtom, lastSwipeInfoAtom } from '@/store/swipe-navigation';

/**
 * 滑动导航 Hook
 * 仅在 App 内使用，全屏检测水平滑动手势
 * 智能识别并避免与卡片滑动冲突
 *
 * 通过 jotai 状态管理触发后退事件，各模块可自行决定是否执行后退操作
 * 不再直接调用 history.back()，避免在某些页面造成异常
 */
export function useEdgeSwipeNavigation() {
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const canNavigateRef = useRef(true);
  const setSwipeBackTrigger = useSetAtom(swipeBackTriggerAtom);
  const setLastSwipeInfo = useSetAtom(lastSwipeInfoAtom);

  useEffect(() => {
    if (!isInApp()) return;

    const MIN_SWIPE_DISTANCE = 100; // 最小滑动距离
    const MIN_HORIZONTAL_RATIO = 1.5; // 水平移动必须是垂直移动的1.5倍以上
    const TOP_SAFE_AREA = 110; // 顶部安全区域高度
    const BOTTOM_SAFE_AREA = 70; // 底部安全区域高度

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const screenHeight = window.innerHeight;

      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now()
      };

      // 检查是否在顶部或底部安全区域内
      const isInTopSafeArea = touch.clientY <= TOP_SAFE_AREA;
      const isInBottomSafeArea = touch.clientY >= screenHeight - BOTTOM_SAFE_AREA;

      // 如果在安全区域内，不响应滑动
      if (isInTopSafeArea || isInBottomSafeArea) {
        canNavigateRef.current = false;
        return;
      }

      // 检查是否在不应该触发导航的元素内
      const target = e.target as HTMLElement;

      // 检查是否有 touch-action 样式（说明元素自己处理手势）
      // 使用 getComputedStyle 更可靠，可以检测到 CSS 类设置的样式
      let hasTouchAction = false;
      let current: HTMLElement | null = target;
      while (current && current !== document.body) {
        const touchAction = window.getComputedStyle(current).touchAction;
        if (touchAction && touchAction !== 'auto' && touchAction !== 'manipulation') {
          hasTouchAction = true;
          break;
        }
        current = current.parentElement;
      }

      // 检查是否在可滑动元素内
      const hasSwipeableParent = target.closest(
        '.embla, .swiper, [data-swipeable], .carousel, [data-carousel]'
      );

      // 检查是否在水平滚动容器内
      const hasHorizontalScroll = target.closest('.overflow-x-auto, .overflow-x-scroll');

      canNavigateRef.current = !hasTouchAction && !hasSwipeableParent && !hasHorizontalScroll;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!canNavigateRef.current) {
        // 重置状态
        canNavigateRef.current = true;
        return;
      }

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const duration = Date.now() - touchStartRef.current.time;

      // 计算移动距离
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // 必须是明显的水平滑动：水平距离 > 最小距离 && 水平距离 > 垂直距离*1.5 && 时长<600ms
      const isHorizontalSwipe =
        absDeltaX > MIN_SWIPE_DISTANCE &&
        absDeltaX > absDeltaY * MIN_HORIZONTAL_RATIO &&
        duration < 600;

      // 满足条件时，通过 jotai 触发后退事件，而不是直接执行 history.back()
      if (isHorizontalSwipe) {
        // 更新滑动信息
        setLastSwipeInfo({
          deltaX,
          deltaY,
          timestamp: Date.now()
        });

        // 递增触发计数器，各模块监听此变化来决定是否执行后退
        setSwipeBackTrigger(prev => prev + 1);
      }

      // 重置状态
      canNavigateRef.current = true;
    };

    const handleTouchCancel = () => {
      canNavigateRef.current = true;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [setSwipeBackTrigger, setLastSwipeInfo]);
}
